/**
 * Contracts Context
 * 
 * Manages agent delivery contracts and packages.
 * Contracts produce delivery pressure signals for the routing engine
 * but NEVER directly assign leads.
 * 
 * Contract Types:
 * - monthly_target: X leads per month
 * - prepaid_package: N leads over Y days
 * - trial: Small volume, short duration promotional
 */

import { createContext, useContext, useState, useCallback, ReactNode, useMemo, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { type VelocityTier, velocityTierToMultiplier } from '@/lib/velocityTier';

// --- Type Definitions ---

export type ContractType = 'monthly_target' | 'prepaid_package' | 'trial';
export type ContractStatus = 'active' | 'fulfilled' | 'expired' | 'breached' | 'at_risk' | 'paused' | 'cancelled';

export interface Contract {
  id: string;
  agentId: string;
  agentEmail: string;
  agentName: string;
  contractType: ContractType;
  totalLeadsCommitted: number;
  cycleStart: number; // timestamp
  cycleEnd: number; // timestamp
  leadsDelivered: number;
  status: ContractStatus;
  priorityTier: VelocityTier;
  createdAt: number;
  notes?: string;
  leadTypes?: string[];
  cancelAtPeriodEnd?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  pricePerLead?: number;
  pausedAt?: string;
  pauseCountThisCycle?: number;
  pausedReason?: string;
  rolloverLeads?: number;
  previousCycleDelivered?: number;
  previousCycleCommitted?: number;
}

// Computed metrics for a contract
export interface ContractMetrics {
  contractId: string;
  leadsRemaining: number;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  expectedDeliveredByNow: number;
  deliveryGap: number; // positive = behind, negative = ahead
  urgencyScore: number; // 0 to 1 (tier-adjusted)
  baseUrgencyScore: number; // 0 to 1 (raw)
  progressPercent: number;
  leadsPerDayNeeded: number;
  isAtRisk: boolean;
  riskReason?: string;
}

// Aggregated pressure for an agent from all their contracts
export interface AgentContractPressure {
  agentId: string;
  activeContracts: number;
  totalCommitted: number;
  totalDelivered: number;
  totalRemaining: number;
  contractPressureScore: number; // 0 to 1, aggregated urgency
  highestUrgencyContract?: Contract;
  atRiskContracts: number;
}

// --- Helper Functions ---

/**
 * Clamp value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Compute metrics for a single contract
 */
export function computeContractMetrics(contract: Contract): ContractMetrics {
  const now = Date.now();
  const totalDays = Math.max(1, Math.ceil((contract.cycleEnd - contract.cycleStart) / (24 * 60 * 60 * 1000)));
  const daysElapsed = Math.max(0, (now - contract.cycleStart) / (24 * 60 * 60 * 1000));
  const daysRemaining = Math.max(0, Math.ceil((contract.cycleEnd - now) / (24 * 60 * 60 * 1000)));

  // Effective target includes rollover leads from previous cycle
  const effectiveTarget = contract.totalLeadsCommitted + (contract.rolloverLeads ?? 0);

  const leadsRemaining = Math.max(0, effectiveTarget - contract.leadsDelivered);
  const progressPercent = effectiveTarget > 0
    ? (contract.leadsDelivered / effectiveTarget) * 100
    : 100;

  // Expected delivery by now (linear pacing against effective target)
  const expectedDeliveredByNow = effectiveTarget * (daysElapsed / totalDays);

  // Delivery gap: positive = behind schedule
  const deliveryGap = expectedDeliveredByNow - contract.leadsDelivered;

  // Leads per day needed to fulfill
  const leadsPerDayNeeded = daysRemaining > 0
    ? leadsRemaining / daysRemaining
    : leadsRemaining;

  // Original expected pace (leads/day over the full cycle)
  const originalPace = effectiveTarget / totalDays;

  // Pace deviation: how much harder we need to work compared to original plan.
  // 0 = on track or ahead, 1 = need 2x the original pace or more.
  const paceRatio = originalPace > 0 ? leadsPerDayNeeded / originalPace : 0;
  const paceIntensity = clamp(paceRatio - 1, 0, 1);

  // Gap ratio: how far behind we are relative to effective target
  const gapRatio = clamp(
    deliveryGap / Math.max(effectiveTarget, 1),
    0,
    1
  );

  // Combine pace + gap so urgency exists from day 1 (pace),
  // and increases if we fall behind (gap).
  const baseUrgencyScore = clamp(
    (paceIntensity * 0.7) + (gapRatio * 0.3),
    0,
    1
  );

  const tierMultiplier = velocityTierToMultiplier(contract.priorityTier ?? 'balanced');
  const urgencyScore = clamp(baseUrgencyScore * tierMultiplier, 0, 1);

  // Risk assessment — velocity-tier-aware thresholds
  // Higher velocity tiers get more lenient thresholds because the system
  // is already routing to them with maximum priority. Marking them at_risk
  // when they're already at max speed is a false positive.
  const tierThresholdMultiplier = 1 / Math.max(tierMultiplier, 0.85);
  const leadsPerDayThreshold = 5 * (1 + (tierMultiplier - 1) * 0.5); // 5 for balanced, 6 for aggressive, 6.5 for max
  const gapThreshold = 0.3 * tierThresholdMultiplier; // 30% for balanced, ~25% for aggressive, ~21% for max

  let isAtRisk = false;
  let riskReason: string | undefined;

  // Check if mathematically impossible at current pace
  if (leadsPerDayNeeded > leadsPerDayThreshold && leadsRemaining > 0) {
    isAtRisk = true;
    riskReason = `Need ${leadsPerDayNeeded.toFixed(1)} leads/day (${leadsRemaining} remaining in ${daysRemaining} days)`;
  }

  // Check if significantly behind — tier-adjusted gap threshold
  if (deliveryGap > effectiveTarget * gapThreshold && daysElapsed > totalDays * 0.5) {
    isAtRisk = true;
    riskReason = riskReason || `${Math.round(deliveryGap)} leads behind schedule at cycle midpoint`;
  }

  // Check if cycle ended with unfulfilled leads
  if (daysRemaining <= 0 && leadsRemaining > 0) {
    isAtRisk = true;
    riskReason = `Cycle ended with ${leadsRemaining} undelivered leads`;
  }

  return {
    contractId: contract.id,
    leadsRemaining,
    daysRemaining,
    daysElapsed: Math.round(daysElapsed * 10) / 10,
    totalDays,
    expectedDeliveredByNow: Math.round(expectedDeliveredByNow * 10) / 10,
    deliveryGap: Math.round(deliveryGap * 10) / 10,
    urgencyScore: Math.round(urgencyScore * 1000) / 1000,
    baseUrgencyScore: Math.round(baseUrgencyScore * 1000) / 1000,
    progressPercent: Math.round(progressPercent * 10) / 10,
    leadsPerDayNeeded: Math.round(leadsPerDayNeeded * 10) / 10,
    isAtRisk,
    riskReason,
  };
}

/**
 * Compute aggregated contract pressure for an agent
 */
export function computeAgentContractPressure(
  agentId: string,
  contracts: Contract[]
): AgentContractPressure {
  const agentContracts = contracts.filter(
    c => c.agentId === agentId && (c.status === 'active' || c.status === 'at_risk')
  );
  
  if (agentContracts.length === 0) {
    return {
      agentId,
      activeContracts: 0,
      totalCommitted: 0,
      totalDelivered: 0,
      totalRemaining: 0,
      contractPressureScore: 0,
      atRiskContracts: 0,
    };
  }
  
  let totalCommitted = 0;
  let totalDelivered = 0;
  let totalRemaining = 0;
  let maxUrgency = 0;
  let highestUrgencyContract: Contract | undefined;
  let atRiskContracts = 0;
  let totalWeightedUrgency = 0;
  
  for (const contract of agentContracts) {
    const metrics = computeContractMetrics(contract);
    
    totalCommitted += contract.totalLeadsCommitted;
    totalDelivered += contract.leadsDelivered;
    totalRemaining += metrics.leadsRemaining;
    
    // Weight urgency by contract size
    totalWeightedUrgency += metrics.urgencyScore * contract.totalLeadsCommitted;
    
    if (metrics.urgencyScore > maxUrgency) {
      maxUrgency = metrics.urgencyScore;
      highestUrgencyContract = contract;
    }
    
    if (metrics.isAtRisk) {
      atRiskContracts++;
    }
  }
  
  // Weighted average urgency across all contracts
  const contractPressureScore = totalCommitted > 0 
    ? clamp(totalWeightedUrgency / totalCommitted, 0, 1) 
    : 0;
  
  return {
    agentId,
    activeContracts: agentContracts.length,
    totalCommitted,
    totalDelivered,
    totalRemaining,
    contractPressureScore: Math.round(contractPressureScore * 1000) / 1000,
    highestUrgencyContract,
    atRiskContracts,
  };
}

/**
 * Get contract pressure scores for all agents with contracts
 */
export function getAllAgentContractPressures(
  contracts: Contract[]
): Map<string, AgentContractPressure> {
  const pressureMap = new Map<string, AgentContractPressure>();
  const agentIds = new Set(contracts.map(c => c.agentId));
  
  for (const agentId of agentIds) {
    const pressure = computeAgentContractPressure(agentId, contracts);
    if (pressure.activeContracts > 0) {
      pressureMap.set(agentId, pressure);
    }
  }
  
  return pressureMap;
}

// --- Context State ---

interface ContractsState {
  contracts: Contract[];
}

interface ContractsActions {
  addContract: (contract: Omit<Contract, 'id' | 'createdAt' | 'status'>) => Promise<Contract>;
  updateContract: (id: string, updates: Partial<Contract>) => void;
  deleteContract: (id: string) => void;
  recordDelivery: (contractId: string, leadCount?: number) => void;
  getAgentContracts: (agentId: string) => Contract[];
  getActiveContracts: () => Contract[];
  getContractMetrics: (contractId: string) => ContractMetrics | null;
  getAgentPressure: (agentId: string) => AgentContractPressure;
  getAllPressures: () => Map<string, AgentContractPressure>;
  updateContractStatuses: () => void;
  refreshContracts: () => Promise<void>;
}

// No mock contracts — real data is loaded from Supabase via refreshContracts()

// --- Context ---

const ContractsContext = createContext<{
  state: ContractsState;
  actions: ContractsActions;
} | undefined>(undefined);

// --- Provider ---

interface ContractsProviderProps {
  children: ReactNode;
}

export function ContractsProvider({ children }: ContractsProviderProps) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const { user } = useAuth();
  const hasRefreshedForUser = useRef<string | null>(null);

  const refreshContracts = useCallback(async () => {
    // Get current auth user and admin status in parallel.
    const [{ data: { user: authUser } }, contractsResult] = await Promise.all([
      supabase.auth.getUser(),
      supabase.from("contracts").select("*"),
    ]);
    const authUid = authUser?.id ?? "";
    const authEmail = authUser?.email ?? "";

    // Check if current user is an admin — admins see ALL contracts.
    let isAdminUser = false;
    if (authUid) {
      const { data: adminRow } = await supabase
        .from("admin_users")
        .select("user_id")
        .eq("user_id", authUid)
        .maybeSingle();
      isAdminUser = !!adminRow;
    }

    const { data, error } = contractsResult;
    console.log("[Contracts] Loaded from Supabase:", { count: data?.length, isAdmin: isAdminUser, error });
    if (error) {
      console.error("[Contracts] Failed to load contracts:", error);
      return;
    }
    if (data) {
      // Admins see all contracts. Agents see only their own (defense-in-depth over RLS).
      // C8 fix: orphan contracts (no agent_id AND no agent_email) are admin-only.
      const visibleRows = isAdminUser
        ? data
        : data.filter((row) => {
            if (row.agent_id === authUid) return true;
            if (authEmail && authEmail.length > 0 && row.agent_email === authEmail) return true;
            return false;
          });

      if (!isAdminUser && visibleRows.length !== data.length) {
        console.warn(`[Contracts] Filtered out ${data.length - visibleRows.length} contracts not owned by current user`);
      }

      setContracts(
        visibleRows.map((row) => ({
          id: row.id,
          agentId: row.agent_id ?? "",
          agentEmail: row.agent_email ?? "",
          agentName: row.agent_name ?? "",
          contractType: row.contract_type as ContractType,
          totalLeadsCommitted: row.total_leads_committed,
          cycleStart: row.cycle_start,
          cycleEnd: row.cycle_end,
          leadsDelivered: row.leads_delivered,
          status: row.status as ContractStatus,
          priorityTier: (row.priority_tier ?? 'balanced') as VelocityTier,
          createdAt: new Date(row.created_at).getTime(),
          notes: row.notes ?? undefined,
          leadTypes: Array.isArray(row.lead_types) ? row.lead_types : undefined,
          cancelAtPeriodEnd: (row as any).cancel_at_period_end ?? false,
          stripeCustomerId: row.stripe_customer_id ?? undefined,
          stripeSubscriptionId: row.stripe_subscription_id ?? undefined,
          pricePerLead: row.price_per_lead ?? undefined,
          pausedAt: (row as any).paused_at ?? undefined,
          pauseCountThisCycle: (row as any).pause_count_this_cycle ?? 0,
          pausedReason: (row as any).paused_reason ?? undefined,
          rolloverLeads: (row as any).rollover_leads ?? 0,
          previousCycleDelivered: (row as any).previous_cycle_delivered ?? undefined,
          previousCycleCommitted: (row as any).previous_cycle_committed ?? undefined,
        }))
      );
    }
  }, []);
  
  // Add a new contract — awaits INSERT and rolls back on failure
  const addContract = useCallback(async (contractData: Omit<Contract, 'id' | 'createdAt' | 'status'>): Promise<Contract> => {
    const newContract: Contract = {
      ...contractData,
      id: `contract-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now(),
      status: 'active',
    };

    // Optimistic UI update
    setContracts(prev => [...prev, newContract]);

    const { error } = await supabase.from("contracts").insert({
      id: newContract.id,
      agent_id: newContract.agentId,
      agent_name: newContract.agentName,
      contract_type: newContract.contractType,
      total_leads_committed: newContract.totalLeadsCommitted,
      cycle_start: newContract.cycleStart,
      cycle_end: newContract.cycleEnd,
      leads_delivered: newContract.leadsDelivered,
      status: newContract.status,
      priority_tier: newContract.priorityTier,
      notes: newContract.notes ?? null,
      lead_types: newContract.leadTypes ?? null,
    });

    if (error) {
      // Rollback optimistic state
      setContracts(prev => prev.filter(c => c.id !== newContract.id));
      console.error("[Contracts] Failed to insert contract, rolled back:", error);
      throw error;
    }

    // Sync with DB to get authoritative state
    refreshContracts();
    return newContract;
  }, [refreshContracts]);

  const deleteContract = useCallback((id: string) => {
    setContracts(prev => prev.filter(c => c.id !== id));
    supabase.from("contracts").delete().eq("id", id).then(({ error }) => {
      if (error) {
        console.error("[Contracts] Failed to delete contract:", error);
        return;
      }
      refreshContracts();
    });
  }, [refreshContracts]);
  
  // Update contract
  const updateContract = useCallback((id: string, updates: Partial<Contract>) => {
    setContracts(prev => prev.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
    supabase.from("contracts").update({
      agent_id: updates.agentId,
      agent_name: updates.agentName,
      contract_type: updates.contractType,
      total_leads_committed: updates.totalLeadsCommitted,
      cycle_start: updates.cycleStart,
      cycle_end: updates.cycleEnd,
      leads_delivered: updates.leadsDelivered,
      status: updates.status,
      priority_tier: updates.priorityTier,
      notes: updates.notes ?? null,
      lead_types: updates.leadTypes,
    }).eq("id", id).then(({ error }) => {
      if (error) {
        console.error("[Contracts] Failed to update contract:", error);
        return;
      }
      refreshContracts();
    });
  }, [refreshContracts]);
  
  // Record a lead delivery for a specific contract.
  // Uses RPC for atomic increment to prevent race conditions under concurrent routing.
  // Falls back to read-then-write if RPC is not available.
  const recordDelivery = useCallback((contractId: string, leadCount: number = 1) => {
    if (!contractId) return;

    // Optimistic local state update
    setContracts(prev => prev.map(c => {
      if (c.id === contractId) {
        const newDelivered = c.leadsDelivered + leadCount;
        const newStatus = newDelivered >= c.totalLeadsCommitted ? 'fulfilled' : c.status;
        return { ...c, leadsDelivered: newDelivered, status: newStatus };
      }
      return c;
    }));

    // Rollback optimistic update if DB write fails
    const rollback = () => {
      setContracts(prev => prev.map(c => {
        if (c.id === contractId) {
          return { ...c, leadsDelivered: Math.max(0, c.leadsDelivered - leadCount) };
        }
        return c;
      }));
      console.warn("[Contracts] Rolled back optimistic delivery update for", contractId);
    };

    // Try atomic increment via RPC first (avoids read-modify-write race condition)
    (supabase.rpc as any)("increment_leads_delivered", {
      p_contract_id: contractId,
      p_count: leadCount,
    }).then(({ error: rpcError }: { error: any }) => {
      if (!rpcError) {
        console.log("[Contracts] Atomic delivery recorded for", contractId, "+", leadCount);
        // Sync UI with server-authoritative state
        refreshContracts();
        return;
      }
      // RPC not available — fall back to read-then-write (less safe but functional)
      console.log("[Contracts] RPC increment not available, using fallback:", rpcError.message);
      supabase.from("contracts")
        .select("leads_delivered, total_leads_committed, status")
        .eq("id", contractId)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error || !data) {
            if (error) console.error("[Contracts] Failed to fetch contract for delivery:", error);
            rollback();
            return;
          }
          const newDelivered = data.leads_delivered + leadCount;
          const newStatus = newDelivered >= data.total_leads_committed ? "fulfilled" : data.status;
          supabase.from("contracts")
            .update({ leads_delivered: newDelivered, status: newStatus })
            .eq("id", contractId)
            .then(({ error: updateError }) => {
              if (updateError) {
                console.error("[Contracts] Failed to record delivery:", updateError);
                rollback();
              } else {
                refreshContracts();
              }
            });
        });
    });
  }, [refreshContracts]);
  
  // Get contracts for a specific agent
  const getAgentContracts = useCallback((agentId: string): Contract[] => {
    return contracts.filter(c => c.agentId === agentId);
  }, [contracts]);
  
  // Get all active contracts
  const getActiveContracts = useCallback((): Contract[] => {
    return contracts.filter(c => c.status === 'active');
  }, [contracts]);
  
  // Get metrics for a specific contract
  const getContractMetrics = useCallback((contractId: string): ContractMetrics | null => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return null;
    return computeContractMetrics(contract);
  }, [contracts]);
  
  // Get aggregated pressure for an agent
  const getAgentPressure = useCallback((agentId: string): AgentContractPressure => {
    return computeAgentContractPressure(agentId, contracts);
  }, [contracts]);
  
  // Get all agent pressures
  const getAllPressures = useCallback((): Map<string, AgentContractPressure> => {
    return getAllAgentContractPressures(contracts);
  }, [contracts]);
  
  // Update contract statuses (check for expired/breached)
  const updateContractStatuses = useCallback(() => {
    const now = Date.now();
    
    setContracts(prev => prev.map(c => {
      if (c.status !== 'active') return c;
      
      // Check if cycle ended
      if (now > c.cycleEnd) {
        if (c.leadsDelivered >= c.totalLeadsCommitted) {
          supabase.from("contracts").update({ status: "fulfilled" }).eq("id", c.id);
          return { ...c, status: 'fulfilled' as ContractStatus };
        } else {
          supabase.from("contracts").update({ status: "breached" }).eq("id", c.id);
          return { ...c, status: 'breached' as ContractStatus };
        }
      }
      
      // Check for at-risk status
      const metrics = computeContractMetrics(c);
      if (metrics.isAtRisk && c.status === 'active') {
        supabase.from("contracts").update({ status: "at_risk" }).eq("id", c.id);
        return { ...c, status: 'at_risk' as ContractStatus };
      }
      
      return c;
    }));
  }, []);

  // Refresh contracts when user authenticates (auth token needed for RLS)
  useEffect(() => {
    if (user && hasRefreshedForUser.current !== user.id) {
      hasRefreshedForUser.current = user.id;
      console.log("[Contracts] Auth user ready, refreshing contracts for:", user.id);
      refreshContracts();
    } else if (!user) {
      hasRefreshedForUser.current = null;
      setContracts([]);
    }
  }, [user, refreshContracts]);
  
  const actions: ContractsActions = useMemo(() => ({
    addContract,
    updateContract,
    deleteContract,
    recordDelivery,
    getAgentContracts,
    getActiveContracts,
    getContractMetrics,
    getAgentPressure,
    getAllPressures,
    updateContractStatuses,
    refreshContracts,
  }), [
    addContract,
    updateContract,
    deleteContract,
    recordDelivery,
    getAgentContracts,
    getActiveContracts,
    getContractMetrics,
    getAgentPressure,
    getAllPressures,
    updateContractStatuses,
    refreshContracts,
  ]);
  
  return (
    <ContractsContext.Provider value={{ state: { contracts }, actions }}>
      {children}
    </ContractsContext.Provider>
  );
}

// --- Hook ---

export function useContracts() {
  const context = useContext(ContractsContext);
  if (!context) {
    throw new Error('useContracts must be used within a ContractsProvider');
  }
  return context;
}

export default ContractsContext;
